import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Youtube } from "lucide-react";
import { Wordmark } from "./Wordmark";
import { useSettings } from "../context/SettingsContext";

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.5 3c.3 2.1 1.6 3.6 3.7 3.9v2.6c-1.3.1-2.5-.3-3.7-1v6.9c0 4.3-3.7 7-7.1 5.8-2.9-1-4.1-4.4-2.6-7 .9-1.6 2.6-2.5 4.4-2.4v2.7c-.4-.1-.9-.1-1.3 0-1.5.4-2 1.9-1.3 3.1.7 1.2 2.6 1.2 3.2-.3.1-.4.2-.8.2-1.2V3h4.1z" />
  </svg>
);

const links = [
  { to: "/setups", label: "Free Setups" },
  { to: "/nerd-scale", label: "Nerd Scale" },
  { to: "/for-beginners", label: "For Beginners" },
  { to: "/articles", label: "Articles" },
  { to: "/my-ai", label: "My AI" },
  { to: "/about", label: "About" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const { settings } = useSettings();
  const loc = useLocation();

  return (
    <header className="sticky top-0 z-50 border-b border-navy/10 bg-bone/90 backdrop-blur-md" data-testid="navbar">
      <div className="mx-auto flex h-[68px] max-w-[1440px] items-center justify-between px-6 lg:px-10">
        <Link to="/" className="no-underline" data-testid="nav-logo" onClick={() => setOpen(false)}>
          <Wordmark style={{ fontSize: 19 }} />
        </Link>

        <nav className="hidden items-center gap-6 lg:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `whitespace-nowrap font-sans text-[13px] font-bold uppercase tracking-[0.1em] no-underline transition-colors ${
                  isActive ? "text-ochre-hover" : "text-navy hover:text-teal"
                }`
              }
              data-testid={`nav-link-${l.label.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Link to="/subscribe" className="btn btn-primary whitespace-nowrap" data-testid="nav-cta">
            Get the setups
          </Link>
        </div>

        <button className="text-navy lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="nav-mobile-toggle">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-navy/10 bg-bone px-6 py-4 lg:hidden" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block py-3 font-sans text-sm font-bold uppercase tracking-[0.14em] text-navy no-underline"
            >
              {l.label}
            </Link>
          ))}
          <Link to="/subscribe" onClick={() => setOpen(false)} className="btn btn-primary mt-3 w-full">
            Get the setups
          </Link>
          <div className="mt-4 flex gap-5">
            <a href={settings.youtube_url} target="_blank" rel="noreferrer" className="text-navy" aria-label="YouTube"><Youtube size={22} /></a>
            <a href={settings.tiktok_url} target="_blank" rel="noreferrer" className="text-navy" aria-label="TikTok"><TikTokIcon size={20} /></a>
          </div>
        </div>
      )}
    </header>
  );
}
